import React, { useState } from "react";
import { Formik, FieldArray, Form } from "formik";
import * as Yup from "yup";
import { TrashIcon, UserPlusIcon } from "@heroicons/react/24/outline";
import {
	CheckCircleIcon,
	PlusIcon,
	ClipboardDocumentIcon,
	ClipboardDocumentCheckIcon,
} from "@heroicons/react/20/solid";
import { useUser } from "@clerk/clerk-react";
import { toast } from "react-toastify";

import useFetch from "../../shared/hooks/useFetch";

import Input from "../../shared/components/FormElements/Input";
import Select from "../../shared/components/FormElements/Select";
import Button from "../../shared/components/FormElements/Button";

const buildTypeformLink = (member, praktijkNaam, userId) => {
	const base = "https://dentiva.typeform.com/mto-onderzoek";
	const naam = `${member.firstName} ${member.lastName}`;
	return `${base}#praktijk=${encodeURIComponent(praktijkNaam)}&naam=${encodeURIComponent(naam)}&functie=${encodeURIComponent(member.function)}&uid=${encodeURIComponent(userId)}`;
};

const CopyLinkRow = ({ member, link }) => {
	const [copied, setCopied] = useState(false);

	const handleCopy = async () => {
		await navigator.clipboard.writeText(link);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	return (
		<div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
			<div className="min-w-0 flex-1">
				<p className="font-medium text-gray-900 truncate">
					{member.firstName} {member.lastName}
				</p>
				<p className="text-sm text-gray-500 truncate">{member.email}</p>
			</div>
			<button
				type="button"
				onClick={handleCopy}
				className="ml-3 flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors shrink-0"
			>
				{copied ? (
					<>
						<ClipboardDocumentCheckIcon className="h-5 w-5 text-green-500" />
						<span className="text-green-600">Gekopieerd</span>
					</>
				) : (
					<>
						<ClipboardDocumentIcon className="h-5 w-5" />
						<span>Kopieer link</span>
					</>
				)}
			</button>
		</div>
	);
};

const InviteTeamForm = ({ closeModal, hasMembers, setHasMembers }) => {
	const inviteMembers = useFetch();
	const { user } = useUser();
	const [invitedMembers, setInvitedMembers] = useState(null);

	const initialValues = {
		teamMembers: [
			{
				firstName: "",
				lastName: "",
				email: "",
				function: "",
			},
		],
	};

	const validationSchema = Yup.object().shape({
		teamMembers: Yup.array().of(
			Yup.object().shape({
				firstName: Yup.string().required("Vul de voornaam in"),
				lastName: Yup.string().required("Vul de achternaam in"),
				email: Yup.string()
					.email("Dit e-mailadres lijkt niet te kloppen")
					.required("Vul het e-mailadres in"),
				function: Yup.string().required("Vul de functie in"),
			})
		),
	});

	const praktijkNaam =
		user.unsafeMetadata?.initialPraktijkNaam ||
		user.publicMetadata?.initialPraktijkNaam;

	const handleSubmit = async (values) => {
		const contactInfo = {
			praktijkNaam,
			praktijkhouder: `${user.firstName} ${user.lastName}`,
			userId: user.id,
			...values,
		};
		try {
			await inviteMembers("/api/tevredenheid/invite", {
				method: "POST",
				body: JSON.stringify(contactInfo),
				headers: {
					"Content-Type": "application/json",
				},
			});

			toast.success(
				`Gelukt! Je hebt ${values.teamMembers.length} teamleden uitgenodigd`,
				{
					position: "bottom-center",
					theme: "light",
					hideProgressBar: true,
					autoClose: 2000,
					icon: (
						<CheckCircleIcon
							className=" text-green-500"
							aria-hidden="true"
						/>
					),
				}
			);

			setInvitedMembers(values.teamMembers);
		} catch (error) {
			console.log(error);
		}
	};

	if (invitedMembers) {
		return (
			<div>
				<div className="flex items-center gap-2 mb-1">
					<CheckCircleIcon className="h-6 w-6 text-green-500" />
					<p className="font-semibold text-gray-900">
						{invitedMembers.length === 1
							? "Teamlid is uitgenodigd!"
							: `${invitedMembers.length} teamleden zijn uitgenodigd!`}
					</p>
				</div>
				<p className="text-sm text-gray-500 mb-4">
					Mocht de e-mail niet aankomen, dan kun je de persoonlijke
					link hieronder kopiëren en handmatig delen.
				</p>
				<div className="bg-gray-50 rounded-lg px-4 py-1 mb-6">
					{invitedMembers.map((member, index) => (
						<CopyLinkRow
							key={index}
							member={member}
							link={buildTypeformLink(
								member,
								praktijkNaam,
								user.id
							)}
						/>
					))}
				</div>
				<Button
					classNames="flex border-2 border-blue-600 w-full text-center center hover:border-blue-500"
					type="button"
					onClick={closeModal}
				>
					Sluiten
				</Button>
			</div>
		);
	}

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={handleSubmit}
		>
			{({ values, handleChange, handleSubmit }) => (
				<Form
					onSubmit={handleSubmit}
					onBlur={() => {
						if (!hasMembers) {
							setHasMembers(true);
						}
					}}
				>
					<FieldArray name="teamMembers">
						{({ push, remove }) => (
							<>
								{values.teamMembers.map((teamMember, index) => (
									<div
										key={index}
										className="border-b-2 pb-10 border-gray-100"
									>
										<p className="text-black font-semibold pl-2 pt-6">
											Teamlid {index + 1}
										</p>
										<div className="flex">
											<div className="w-1/2">
												<Input
													label="Voornaam"
													name={`teamMembers[${index}].firstName`}
													value={teamMember.firstName}
													onChange={handleChange}
												/>
											</div>
											<div className="w-1/2">
												<Input
													label="Achternaam"
													name={`teamMembers[${index}].lastName`}
													value={teamMember.lastName}
													onChange={handleChange}
												/>
											</div>
										</div>

										<Input
											label="E-mailadres"
											name={`teamMembers[${index}].email`}
											value={teamMember.email}
											onChange={handleChange}
										/>
										<div className="w-1/2">
											<Select
												label="Functie"
												name={`teamMembers[${index}].function`}
												value={teamMember.function}
												onChange={handleChange}
												options={[
													"Tandarts",
													"Mondhygiënist",
													"Praktijkmanager",
													"Assistente",
													"Preventieassistente",
													"Ortho",
													"Balieassistente",
													"Paropreventie",
													"Overige",
												]}
											/>
										</div>
										{index > 0 && (
											<button
												type="button"
												onClick={() => remove(index)}
												className="ml-1.5 mt-4 flex text-sm font-medium hover:scale-105 w-fit"
											>
												<TrashIcon className="h-5 w-5 mr-2 text-red-600" />
												<span className="text-gray-600 hover:text-black">
													Teamlid verwijderen
												</span>
											</button>
										)}
									</div>
								))}
								<Button
									type="button"
									onClick={() =>
										push({
											firstName: "",
											lastName: "",
											email: "",
											function: "",
										})
									}
									classNames="flex font-medium bg-white  hover:bg-white border-2 border-blue-600 hover:border-blue-600 hover:scale-105 w-fit"
								>
									<PlusIcon className="h-6 w-6 mr-2 text-blue-600" />
									<span className="text-blue-600">
										Nog een teamlid
									</span>
								</Button>
							</>
						)}
					</FieldArray>
					<Button
						classNames="flex border-2 border-blue-600 mr-2 w-full text-center center hover:border-blue-500"
						type="submit"
					>
						<UserPlusIcon className="h-6 w-6 mr-2 text-white" />
						{values.teamMembers.length === 1
							? "Teamlid uitnodigen"
							: `Deze ${values.teamMembers.length} teamleden
						uitnodigen`}
					</Button>
				</Form>
			)}
		</Formik>
	);
};

export default InviteTeamForm;
